import classNames from "classnames";
import "./style.scss";
import OliverText from "@builder/components/general/OliverText";
import { FieldValues, RegisterOptions, UseFormRegister } from "react-hook-form";
import { useTranslation } from "react-i18next";

interface TextInputProps {
    title?: string;
    placeholder?: string;
    value?: string;
    callback: (value: string) => void;
    type?: "input" | "change";
    icon?: { src: string | string[]; callback?: (e: React.MouseEvent) => void };
    icon2?: { src: string | string[]; callback?: (e: React.MouseEvent) => void };
    filter?: RegExp;
    layout?: "row" | "column";
    onClick?: (e?: React.MouseEvent) => void;
    readOnly?: boolean;
    name?: string;
    register?: UseFormRegister<FieldValues>;
    rules?: RegisterOptions;
    error?: boolean;
    className?: string;
    isPercent?: boolean;
    noTranslate?: boolean;
    suffix?: string;
    disabled?: boolean;
}

const TextInput: React.FC<TextInputProps> = ({
    title,
    placeholder,
    value,
    callback,
    type = "input",
    icon,
    icon2,
    filter,
    layout = "column",
    onClick,
    readOnly,
    name,
    register,
    disabled,
    rules,
    error = false,
    className,
    isPercent = false,
    noTranslate,
    suffix
}: TextInputProps) => {
    const { t } = useTranslation();
    // so it doesn't break the rest which doesn't have the register.
    const { onChange, ...rest } = register?.(name || "", { ...rules }) ?? { onChange: () => {} };

    const handleInput = (e: React.FormEvent<HTMLInputElement>): void => {
        const value = e.currentTarget.value;

        if (typeof value === "string") {
            if (filter) {
                if (filter.test(value)) {
                    callback(value);
                    void onChange(e);
                }
            } else {
                callback(value);
                void onChange(e);
            }
        }
    };

    const inputLabelClass = classNames("text-input-label", {
        ["layout-row"]: layout === "row",
        ["layout-column"]: layout === "column",
        [`${className}`]: className
    });

    const inputWrapperClass = classNames("input-wrapper", {
        ["w-icon"]: icon,
        ["w-icon2"]: icon2?.src ? true : false,
        ["disabled"]: disabled,
        [`${className}`]: className
    });

    const iconWrapperClass = classNames("icon-wrapper", {
        ["clickable"]: icon?.callback && !disabled
    });

    const icon2WrapperClass = classNames("icon-wrapper icon2-wrapper", {
        ["clickable"]: icon2?.callback && !disabled
    });

    const suffixWrapperClass = classNames("icon-wrapper", {
        ["suffix"]: suffix
    });

    return (
        <label className={inputLabelClass}>
            {title && <OliverText text={noTranslate ? title : t("generalComponents.textInput." + title)} size="xs" />}
            <div className={inputWrapperClass} onClick={onClick}>
                {icon2?.src && (
                    <span className={icon2WrapperClass}>
                        <div onClick={icon2.callback && icon2.callback}>
                            <img src={icon2.src as string} />
                        </div>
                    </span>
                )}
                {icon && (
                    <span className={iconWrapperClass}>
                        {Array.isArray(icon.src) ? (
                            icon.src.map((src, index) => (
                                <div className="multi-icon-wrapper" key={index}>
                                    <div onClick={icon.callback && icon.callback}>
                                        <img
                                            src={src}
                                            className={`
                                            ${src.includes("dollar-sign") && !isPercent ? "active" : ""}
                                            ${src.includes("percentage-sign") && isPercent ? "active" : ""}
                                        `}
                                        />
                                    </div>
                                    {index < icon.src.length - 1 && (
                                        <div style={{ margin: "0px 10px", height: "14px", borderLeft: "1px solid #ABA7AF" }} />
                                    )}
                                </div>
                            ))
                        ) : (
                            <div onClick={icon.callback && icon.callback}>
                                <img src={icon.src} />
                            </div>
                        )}
                    </span>
                )}
                {suffix && <span className={suffixWrapperClass}>{suffix}</span>}
                {type === "input" ? (
                    <input
                        className={`${className} ${error ? "error" : ""} `}
                        type="text"
                        placeholder={placeholder && (noTranslate ? placeholder : t("generalComponents.textInput." + placeholder))}
                        value={value || ""}
                        onInput={handleInput}
                        readOnly={readOnly}
                        {...rest}
                    />
                ) : (
                    <input
                        className={error ? "error" : ""}
                        type="text"
                        placeholder={placeholder && t("generalComponents.textInput." + placeholder)}
                        value={value || ""}
                        onChange={handleInput}
                        readOnly={readOnly}
                        {...rest}
                    />
                )}
            </div>
        </label>
    );
};

export default TextInput;
